import { useEffect, useState } from "react";

import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import useCacheCleaner from "@/Common/hooks/CacheCleaner";

// THIRD PARTY
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";

// SUBSCRIPTIONS
import { useSubscriptions } from "@/Common/context/subscriptions/Subscriptions";

// QUERIES
import { getUnEnrolledEmployeesByLocation, getUsersWithQueueStatus, getQueues, getQueueLabels } from "@/Settings/api/QueueMapping/queries";

// MUTATIONS
import { createQueue, deleteQueue, updateQueue } from "@/Settings/api/QueueMapping/mutations";

// SUBSCRIPTIONS
import subscriptionQueries from "@/Settings/api/QueueMapping/subscriptionQueries";

// TYPES
import {
	ICreateQueueResponse,
	IDeleteQueueResponse,
	IManagedPosition,
	IPositionQueue,
	IQueue,
	IQueueMappingSidebarItem,
	IUpdateQueueResponse,
	IUserQueue,
	IUserWithQueue,
} from "@/Settings/types/QueueMapping/QueueMapping";

import { errorLogger } from "@/Common/helpers/logger";

// CONSTANTS
import { DEFAULT_QUEUE_NAME, OTHER_EMPLOYEES_POSITION, QUEUE_EMPTY_VALUE } from "@/Settings/Constants/QueueMapping";

// HELPERS
import {
	createDefaultQueue,
	getLabels,
	getOrderedEmployees,
	getTransformedUsers,
	getUniqueUsers,
	validateName,
	validateOverflowName,
	getUpdatedUsersAtHandleSelect,
} from "@/Settings/helpers/QueueMapping";
import { getCommissionCycles } from "@/Common/api/commissionCycles";
import { getCurrentCommissionCycle } from "@/Common/helpers/actions";
import QueueMappingWrapper from "./QueueMappingWrapper";

export default function QueueMappingLayer() {
	const { clearCache } = useCacheCleaner();
	const location = useSelector((state: RootState) => state.location.location);
	const locationId = location?.locationId ?? "";

	const { addSubscription, getSubscription } = useSubscriptions();
	const queryClient = useQueryClient();

	const [isEditMode, setIsEditMode] = useState<boolean>(false);
	const [queue, setQueue] = useState<IQueue>(createDefaultQueue(locationId));
	const [selectedPositions, setSelectedPositions] = useState<IManagedPosition[]>([]);
	const [selectedUsers, setSelectedUsers] = useState<IPositionQueue[]>([]);
	const [confirmModalState, setConfirmModalState] = useState<boolean>(false);

	const shouldShowEmpty = !queue._id && !isEditMode;
	const shouldShowDetails = Boolean(queue._id || isEditMode);

	//************************************ (SUBSCRIPTIONS) ************************************
	const queueSubscriptions = getSubscription(["queueSubscriptions", locationId]);
	queueSubscriptions?.setInvalidate(["queueSubscriptions", locationId]);

	queueSubscriptions?.subscriptions?.onCreateQueue?.setCallback((response: ICreateQueueResponse) => {
		queryClient.setQueryData(["queues", locationId], (cachedData) => {
			if (response.data.onCreateQueue === undefined) {
				return cachedData;
			}
			if (!cachedData || (cachedData as IQueue[]).length === 0) {
				return [response.data.onCreateQueue];
			}
			if ((cachedData as IQueue[]).some((qu) => qu._id === response.data.onCreateQueue._id)) {
				return (cachedData as IQueue[]).map((qu) => {
					return qu._id === response.data.onCreateQueue._id ? response.data.onCreateQueue : qu;
				});
			}
			return [...(cachedData as IQueue[]), response.data.onCreateQueue];
		});
	});

	queueSubscriptions?.subscriptions?.onUpdateQueue?.setCallback((response: IUpdateQueueResponse) => {
		queryClient.setQueryData(["queues", locationId], (cachedData) => {
			if (!cachedData) {
				return [response.data.onUpdateQueue];
			}
			return (cachedData as IQueue[]).map((qu) => {
				return qu._id === response.data.onUpdateQueue._id ? response.data.onUpdateQueue : qu;
			});
		});
	});

	queueSubscriptions?.subscriptions?.onDeleteQueue?.setCallback((response: IDeleteQueueResponse) => {
		queryClient.setQueryData(["queues", locationId], () => {
			return response.data.onDeleteQueue.queues;
		});
	});

	//************************************ (QUERIES) ************************************

	const queryResults = useQueries({
		queries: [
			// Get commission Cycles
			{
				queryKey: ["commissionCycles", locationId],
				queryFn: () => getCommissionCycles(locationId),
				staleTime: Infinity,
				enabled: !!locationId,
			},
			// GET queues
			{
				queryKey: ["queues", locationId],
				queryFn: () => getQueues(locationId),
				staleTime: Infinity,
				enabled: Boolean(locationId),
			},
			// Get users by managed positions and also with queue status
			{
				queryKey: ["usersByPosition", locationId, selectedPositions],
				queryFn: () => getUsersWithQueueStatus(locationId, getLabels(selectedPositions)),
				staleTime: Infinity,
				enabled: Boolean(selectedPositions.length && locationId),
			},
			// Get employees by location
			{
				queryKey: ["QueueMappingEmployees", locationId],
				queryFn: async () => {
					if (!location?.locationId) {
						return [];
					}

					const response = await getUnEnrolledEmployeesByLocation(location?.locationId);

					if (!response) {
						return [];
					}

					const transformedEmployees = getTransformedUsers(response);

					return getOrderedEmployees(transformedEmployees);
				},

				enabled: !!location?.locationId,
				staleTime: Infinity,
			},
		],
	});

	const USE_QUERY_GET_COMMISSION_CYCLES = 0;
	const USE_QUERY_GET_QUEUES = 1;
	const USE_QUERY_GET_USERS_BY_POSITION = 2;
	const USE_QUERY_GET_EMPLOYEES = 3;

	const { data: commissionCycles } = queryResults[USE_QUERY_GET_COMMISSION_CYCLES];
	const { data: queues, isLoading: isLoadingQueues } = queryResults[USE_QUERY_GET_QUEUES];
	const { data: associates, isLoading: isUsersLoading } = queryResults[USE_QUERY_GET_USERS_BY_POSITION];
	const { data: employees, isLoading: isEmployeesLoading } = queryResults[USE_QUERY_GET_EMPLOYEES];

	const currentCommissionCycle = commissionCycles ? getCurrentCommissionCycle(commissionCycles) : null;

	const { data: queueLabels, isLoading: isQueueLabelsLoading } = useQuery({
		queryKey: ["queueLabels", locationId, currentCommissionCycle],
		queryFn: () => getQueueLabels(locationId, currentCommissionCycle?._id),
		staleTime: Infinity,
		enabled: Boolean(locationId && currentCommissionCycle && queues),
	});

	//************************************ (MUTATIONS) ************************************
	// `CREATE` queue mutation
	const { mutate: mutateCreateQueue } = useMutation({
		mutationKey: ["createQueue", locationId],
		mutationFn: (input: IQueue) => createQueue(locationId, input),
		onSuccess: (data) => {
			if (!data) {
				return;
			}
			setQueue(data as unknown as IQueue);
			queryClient.setQueryData(["queues", locationId], (oldData: IQueue[] | undefined) => (oldData ? [...oldData, data] : [data]));
		},
		onError: (error) => {
			errorLogger(error, { function: "mutateCreateQueue", message: "Failed to create queue." });
		},
	});

	// `UPDATE` queue mutation
	const { mutate: mutateUpdateQueue } = useMutation({
		mutationKey: ["updateQueue", locationId],
		mutationFn: (input: IQueue) => updateQueue(locationId, input),
		onSuccess: (data) => {
			if (!data) {
				return;
			}
			queryClient.setQueryData(["queues", locationId], (oldData: IQueue[] | undefined) =>
				oldData?.map((queueItem) => (queueItem._id === data._id ? data : queueItem))
			);
		},
		onError: (error) => {
			errorLogger(error, { function: "mutateUpdateQueue", message: "Failed to update queue." });
		},
	});

	// `DELETE` queue mutation
	const { mutate: mutateDeleteQueue } = useMutation({
		mutationKey: ["deleteQueue", locationId],
		mutationFn: (input: IQueue[]) => deleteQueue(locationId, input),
		onSuccess: (data) => {
			if (!data) {
				return;
			}
			queryClient.setQueryData(["queues", locationId], () => {
				return data.data.onDeleteQueue.queues;
			});
		},
		onError: (error) => {
			errorLogger(error, { function: "mutateDeleteQueue", message: "Failed to delete queue." });
		},
	});

	//************************************ (HANDLER FUNCTIONS) ************************************

	const uniqueUsers = queues ? getUniqueUsers(queues) : [];

	const handleQueueName = (title: string) => {
		setQueue({ ...queue, name: title });
		handleMutateQueue({ ...queue, name: title });
	};

	const handleUpdateOverflowQueues = (overflowQueues: string[]) => {
		const existSomeOverflowWithError = validateOverflowName(overflowQueues, queues);

		if (existSomeOverflowWithError) {
			return;
		}

		setQueue({ ...queue, overflowQueues });
		handleMutateQueue({ ...queue, overflowQueues });
	};

	const handleMutateQueue = (updatedQueue: IQueue) => {
		const hasQueueValidName = validateName(updatedQueue, queues);

		if (!hasQueueValidName || updatedQueue.usersByPosition.length === 0) {
			return;
		}
		if (updatedQueue._id) {
			mutateUpdateQueue(updatedQueue);
			return;
		}
		mutateCreateQueue({ ...updatedQueue, locationId });
	};

	const onPositionsChange = (selected: IManagedPosition[]) => {
		setSelectedPositions(selected);
		clearCache(["usersByPosition"]);
	};

	const onDeletePosition = (positionId: string) => {
		const updatedQueueUsers = queue.usersByPosition.filter((pos) => pos._id !== positionId);
		const selectedPositionUpdated = selectedPositions.filter((pos) => pos._id !== positionId);
		setQueue({ ...queue, usersByPosition: updatedQueueUsers });
		setSelectedPositions(selectedPositionUpdated);
		handleMutateQueue({ ...queue, usersByPosition: updatedQueueUsers });
	};

	const shouldRemoveUser = (user: IUserQueue, usersToRemove: IPositionQueue[]): boolean => {
		return usersToRemove.some((posToReview) => posToReview.users.some((u) => u._id === user._id));
	};

	const onDeleteMembers = (usersToRemove: IPositionQueue[]) => {
		const updatedQueuePositions = queue.usersByPosition.map((pos) => {
			const users = pos.users;
			const updatedUsers = users.filter((user) => !shouldRemoveUser(user, usersToRemove));
			return { ...pos, users: updatedUsers };
		});

		// Check if user is the last one in the position
		const queuePositions = updatedQueuePositions.filter((pos) => pos.users.length > 0);

		setQueue({ ...queue, usersByPosition: queuePositions });

		const updatedSelectedUsersByPositions = updatedQueuePositions.map((pos) => {
			return {
				...pos,
				users: pos.users.filter((user) => shouldRemoveUser(user, usersToRemove)),
			};
		});

		setSelectedUsers(updatedSelectedUsersByPositions.filter((pos) => pos.users.length > 0));

		const updatedSelectedPositions = queuePositions
			.filter((pos) => pos.name !== OTHER_EMPLOYEES_POSITION)
			.map((pos) => ({ _id: pos.name.replaceAll(" ", ""), label: pos.name }));

		setSelectedPositions(updatedSelectedPositions);

		handleMutateQueue({ ...queue, usersByPosition: queuePositions });
	};

	const updatePositionQueue = (selections: IUserWithQueue[], positionName: string) => {
		const isPositionIsAlreadyInQueue = queue.usersByPosition.some((pos) => pos.name === positionName);

		let updatedUsersByPosition: IPositionQueue[] = [];
		if (!isPositionIsAlreadyInQueue) {
			const users = selections.map((user) => ({ _id: user._id, name: user.fullName }));
			const positionQueue: IPositionQueue = {
				_id: positionName.replaceAll(" ", ""),
				name: positionName,
				users,
			};

			updatedUsersByPosition = [...queue.usersByPosition, positionQueue];
		} else {
			const currentPosition = queue.usersByPosition.find((pos) => pos.name === positionName);

			const newUsers = selections
				.filter((selectedUser) => !currentPosition?.users.find((userInQueue) => userInQueue._id === selectedUser._id))
				.map((selectedUser) => ({ _id: selectedUser._id, name: selectedUser.fullName }));

			currentPosition?.users.push(...newUsers);

			updatedUsersByPosition = queue.usersByPosition.map((pos) =>
				pos.name === positionName ? currentPosition : pos
			) as IPositionQueue[];
		}
		return updatedUsersByPosition;
	};

	const onAssociatesChange = (selections: IUserWithQueue[]) => {
		if (!selectedPositions.length || !selections?.length) {
			return;
		}

		const updatedUsersByPositions: IPositionQueue[] = selectedPositions.reduce((acc: IPositionQueue[], position) => {
			const usersByPosition = selections.filter((user) => user.position === position.label);
			if (!usersByPosition.length) {
				return acc;
			}
			const updatedPositionQueue = updatePositionQueue(usersByPosition, position.label);
			return [...acc, ...updatedPositionQueue];
		}, []);

		// Filter out duplicates
		const uniqueUpdatedUsersByPositions = updatedUsersByPositions.filter(
			(item, index, self) => index === self.findIndex((t) => t._id === item._id)
		);

		// Move OTHER_EMPLOYEES_POSITION to the end of the array
		const otherEmployeesPosition = uniqueUpdatedUsersByPositions.find((pos) => pos.name === OTHER_EMPLOYEES_POSITION);
		if (otherEmployeesPosition) {
			const otherEmployeesPositionIndex = uniqueUpdatedUsersByPositions.indexOf(otherEmployeesPosition);
			if (otherEmployeesPositionIndex > -1) {
				uniqueUpdatedUsersByPositions.splice(otherEmployeesPositionIndex, 1);
				uniqueUpdatedUsersByPositions.push(otherEmployeesPosition);
			}
		}

		setQueue({ ...queue, usersByPosition: uniqueUpdatedUsersByPositions });
		handleMutateQueue({ ...queue, usersByPosition: uniqueUpdatedUsersByPositions });
	};

	const onEmployeesChange = (selections: IUserWithQueue[]) => {
		if (!selections?.length) {
			return;
		}
		const updatedUsersByPositions = updatePositionQueue(selections, OTHER_EMPLOYEES_POSITION);

		setQueue({ ...queue, usersByPosition: updatedUsersByPositions });
		handleMutateQueue({ ...queue, usersByPosition: updatedUsersByPositions });
	};

	const handleSelectUser = (position: string, user: IUserQueue) => {
		const isPositionIsAlreadySelected = selectedUsers.some((pos) => pos.name === position);

		if (!isPositionIsAlreadySelected) {
			const newPosition: IPositionQueue = {
				_id: position.replaceAll(" ", ""),
				name: position,
				users: [user],
			};
			setSelectedUsers((prev) => [...prev, newPosition]);
			return;
		}

		const updatedUsers = getUpdatedUsersAtHandleSelect(selectedUsers, position, user);

		setSelectedUsers(updatedUsers);
	};

	const onAddQueue = () => {
		setIsEditMode(true);
		setQueue(QUEUE_EMPTY_VALUE);
		setSelectedPositions([]);
	};
	const onDeleteQueue = () => {
		if (!queue._id) {
			return;
		}
		mutateDeleteQueue([queue]);
		setQueue(QUEUE_EMPTY_VALUE);
		setIsEditMode(false);
	};
	const onSelectQueue = (selectedItem: IQueueMappingSidebarItem) => {
		if (!queues) {
			return;
		}
		const selectedQueue = queues.find((q: IQueue) => q._id === selectedItem._id);

		if (!selectedQueue) {
			return;
		}

		setQueue(selectedQueue);
		setIsEditMode(true);
		const selectedQueuePositions = selectedQueue.usersByPosition
			.filter((pos: IPositionQueue) => pos.name !== OTHER_EMPLOYEES_POSITION && pos.users.length > 0)
			.map((pos: IPositionQueue) => ({ _id: pos._id, label: pos.name }));
		setSelectedPositions(selectedQueuePositions);
		setSelectedUsers([]);
	};

	const handleCopyQueue = () => {
		setQueue({ ...queue, _id: "", name: DEFAULT_QUEUE_NAME, overflowQueues: [] });
		setIsEditMode(true);
	};

	const handleConfirmModalToDeleteUnusedQueues = (state: boolean) => {
		setConfirmModalState(state);
	};

	const handleDeleteUnusedQueues = () => {
		const queuesToDelete = queues?.filter((q: IQueue) => !queueLabels?.includes(q.name));
		if (!queuesToDelete) {
			return;
		}

		mutateDeleteQueue(queuesToDelete);
		setQueue(QUEUE_EMPTY_VALUE);
		setIsEditMode(false);
		setConfirmModalState(false);
	};

	useEffect(() => {
		if (!locationId) {
			return;
		}
		addSubscription(["queueSubscriptions", locationId], subscriptionQueries(locationId));
	}, [addSubscription, locationId]);

	return (
		<QueueMappingWrapper
			queueLabels={queueLabels}
			queue={queue}
			queues={queues}
			onAddQueue={onAddQueue}
			onDeleteQueue={onDeleteQueue}
			onSelectQueue={onSelectQueue}
			handleCopyQueue={handleCopyQueue}
			handleQueueName={handleQueueName}
			handleUpdateOverflowQueues={handleUpdateOverflowQueues}
			selectedPositions={selectedPositions}
			onPositionsChange={onPositionsChange}
			onDeletePosition={onDeletePosition}
			onDeleteMembers={onDeleteMembers}
			associates={associates}
			employees={employees}
			selectedUsers={selectedUsers}
			onAssociatesChange={onAssociatesChange}
			onEmployeesChange={onEmployeesChange}
			handleSelectUser={handleSelectUser}
			isLoadingQueues={isLoadingQueues}
			isQueueLabelsLoading={isQueueLabelsLoading}
			isEmployeesLoading={isEmployeesLoading}
			isUsersLoading={isUsersLoading}
			uniqueUsers={uniqueUsers}
			shouldShowEmpty={shouldShowEmpty}
			shouldShowDetails={shouldShowDetails}
			confirmModalState={confirmModalState}
			handleConfirmModalState={handleConfirmModalToDeleteUnusedQueues}
			handleDeleteUnusedQueues={handleDeleteUnusedQueues}
		/>
	);
}
